import { useEffect, useMemo, useState } from "react";
import { generatePath, useLocation, useNavigate } from "react-router";

import { m } from "@vega-core/paraglide/messages";
import { scrollToElementWithOffset } from "@vega-core/utils/scrollToElementWithOffset";

import { ARTICLE_ROUTE_TEMPLATE } from "../constants/routes";
import { type Article } from "../types/article";
import { type FAQSection } from "../types/faq";
import { getPublishedArticles } from "../utils/getPublishedArticles";

import { ResourcesArticles } from "./ResourcesArticles";
import { ResourcesFaqs } from "./ResourcesFaqs";
import { ResourcesPageLayout } from "./ResourcesPageLayout";
import { ResourcesSearch } from "./ResourcesSearch";

type Props = {
  articles: Article[];
  faqSections: FAQSection[];
  title: string;
  description?: string;
  searchLabels?: ResourcesSearchLabels;
};

type ResourcesSearchLabels = {
  articlesGroupLabel: string;
  faqGroupLabel: string;
  faqSectionLabel: string;
};

export const ResourcesPage = ({
  articles,
  faqSections,
  title,
  description,
  searchLabels,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const publishedArticles = getPublishedArticles(articles);
  const [openFaqSection, setOpenFaqSection] = useState<number | undefined>();
  const searchLabelsValue = searchLabels ?? {
    articlesGroupLabel: m.cms_schemas_articles_title(),
    faqGroupLabel: m.helpCentre_faqs_title(),
    faqSectionLabel: m.cms_fields_faqSection_title(),
  };

  const faqSectionItems = useMemo(
    () =>
      faqSections.map((section) => ({
        ...section,
        items: section.items.map((item, itemIndex) => ({
          ...item,
          id: `faq-${section.value}-${itemIndex}`,
        })),
      })),
    [faqSections],
  );

  const getArticlePath = (slug: string) =>
    generatePath(ARTICLE_ROUTE_TEMPLATE, { slug });
  const handleArticleClick = (slug: string) => {
    navigate(getArticlePath(slug));
  };

  const scrollToTarget = (id: string) => {
    scrollToElementWithOffset(id, { offset: 96, behavior: "smooth" });
  };

  const setHash = (targetId: string) => {
    if (location.hash !== `#${targetId}`) {
      navigate({ hash: `#${targetId}` }, { replace: true });
    }
  };

  const handleFaqClick = (sectionIndex: number, targetId: string) => {
    setOpenFaqSection(sectionIndex);
    setHash(targetId);
    setTimeout(() => scrollToTarget(targetId), 0);
  };

  useEffect(() => {
    const hash = decodeURIComponent(location.hash.slice(1));

    if (!hash) {
      return;
    }

    const sectionIndex = faqSectionItems.findIndex((section) =>
      section.items.some((item) => item.id === hash),
    );

    if (sectionIndex !== -1) {
      setOpenFaqSection(sectionIndex);
      setTimeout(() => scrollToTarget(hash), 0);
    }
  }, [faqSectionItems, location.hash]);

  return (
    <ResourcesPageLayout>
      <div className="space-y-8">
        <div>
          <h1 className="heading-2 text-text/primary mb-2">{title}</h1>
          {description ? (
            <p className="body-base-regular text-text/secondary">
              {description}
            </p>
          ) : null}
        </div>
        <ResourcesSearch
          articles={publishedArticles}
          faqSections={faqSectionItems}
          labels={searchLabelsValue}
          onArticleSelect={handleArticleClick}
          onFaqSelect={handleFaqClick}
        />
        <h2 className="heading-4 text-text/primary">
          {m.cms_schemas_articles_title()}
        </h2>
        <ResourcesArticles
          articles={publishedArticles}
          onArticleClick={handleArticleClick}
        />
        <section className="space-y-8">
          <h2 className="body-large-semibold text-text/primary">
            {m.localcms_resources_frequently_asked_questions()}
          </h2>
          <ResourcesFaqs
            onToggleSection={setOpenFaqSection}
            openSectionIndex={openFaqSection}
            sections={faqSectionItems}
          />
        </section>
      </div>
    </ResourcesPageLayout>
  );
};
